import { useMemo, useRef } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { FaBoxOpen, FaCubes, FaDollarSign, FaTruck, FaTag, FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import api from '../../services/api';
import ModalForm from '../modal-form';
import { TipoFormulario } from '../../services/constantes';
import { ApiError, Campo, Estoque } from '../../services/tipos';

type TipoMovimentacao = 'repor' | 'debitar';

interface ModalMovimentacaoProps {
    open: boolean;
    onClose: () => void;
    onSucesso: () => void;
    tipo: TipoMovimentacao;
    estoque: (Estoque & { peca?: any }) | null;
}

const formatarReais = (v: number | undefined | null) =>
    `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

export default function ModalMovimentacao({ open, onClose, onSucesso, tipo, estoque }: ModalMovimentacaoProps) {
    const qtdAtual = Number(estoque?.quantidade) || 0;
    const ehRepor = tipo === 'repor';
    const entidadeRef = useRef<any>({});

    const titulo = ehRepor ? 'Repor Estoque' : 'Debitar Estoque';
    const tituloBotao = ehRepor ? 'Repor' : 'Debitar';
    const corBotao = ehRepor ? '#2e7d32' : '#c62828';
    const labelAcao = ehRepor ? 'repor' : 'debitar';
    const IconeAcao = ehRepor ? FaPlusCircle : FaMinusCircle;

    const corCard = ehRepor
        ? { bg: '#e6f7ec', borda: '#2e7d32', texto: '#1b5e20' }
        : { bg: '#fdecea', borda: '#c62828', texto: '#7f1d1d' };

    const ResumoEstoque = () => {
        const peca = estoque?.peca;
        const nomePeca = peca ? `${peca.marca} ${peca.modelo} - ${peca.nome}` : '—';
        const fornecedor = estoque?.prestadorServico?.nome;
        const identificador = estoque?.identificador;

        return (
            <div style={{
                padding: '1rem 1.2rem',
                borderRadius: '8px',
                background: corCard.bg,
                borderLeft: `5px solid ${corCard.borda}`,
                color: corCard.texto,
                fontSize: '0.9rem',
                lineHeight: '1.8',
            }}>
                <div style={{ fontWeight: 700, marginBottom: '0.6rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <IconeAcao size={16} /> Movimentação de Estoque
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaBoxOpen size={14} /> <span><b>Peça:</b> {nomePeca}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaCubes size={14} /> <span><b>Quantidade atual em estoque:</b> {qtdAtual}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaDollarSign size={14} /> <span><b>Valor unitário:</b> {formatarReais(estoque?.valor)}</span>
                </div>
                {fornecedor && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaTruck size={14} /> <span><b>Fornecedor:</b> {fornecedor}</span>
                    </div>
                )}
                {identificador && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaTag size={14} /> <span><b>Identificador:</b> {identificador}</span>
                    </div>
                )}
            </div>
        );
    };

    const campos: Campo[] = useMemo(() => [
        {
            coluna: 1, largura: 12, nome: '_resumo', tipo: 'custom', linha: 1, required: false, visible: true, label: '',
            component: () => <ResumoEstoque />,
        },
        {
            coluna: 1, label: `Quantidade a ${labelAcao}`, largura: 12, nome: 'quantidade', tipo: 'decimal', linha: 2, required: true, visible: true,
            ajudinha: ehRepor
                ? { texto: 'Quantidade que será SOMADA ao estoque atual.' }
                : { texto: `Quantidade que será SUBTRAÍDA do estoque atual. Não pode ultrapassar ${qtdAtual}.` },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [qtdAtual, labelAcao, ehRepor, tipo, estoque?.id]);

    const handleSalvar = async (entidade: any) => {
        if (!estoque?.id) return;

        const quantidade = Number(String(entidade.quantidade ?? '').replace(',', '.'));
        if (!quantidade || quantidade <= 0) {
            toast.error('Informe uma quantidade maior que zero.');
            return;
        }
        if (!ehRepor && quantidade > qtdAtual) {
            toast.error(`Não pode debitar mais que o estoque atual (${qtdAtual}).`);
            return;
        }

        try {
            const rota = ehRepor ? 'Repor' : 'Debitar';
            await api.post(`/Estoque/${estoque.id}/${rota}`, { quantidade });
            toast.success(ehRepor ? 'Estoque reposto com sucesso.' : 'Estoque debitado com sucesso.');
            onSucesso();
            onClose();
        } catch (error) {
            const axiosError = error as AxiosError<ApiError>;
            const msg = axiosError.response?.data?.Mensagem || 'Erro ao movimentar estoque.';
            toast.error(msg);
        }
    };

    return (
        <ModalForm
            modo={TipoFormulario.tpCadastro}
            titulo={titulo}
            tituloBotaoOk={tituloBotao}
            corBotaoOk={corBotao}
            tamanho="lg"
            campos={campos}
            onSave={handleSalvar}
            show={open}
            carregando={false}
            onHide={onClose}
            onCancel={onClose}
            entidadeSelecionada={entidadeRef.current}
            ajudinha={{
                texto: ehRepor
                    ? 'Informe a quantidade comprada / reposta para somar ao estoque atual.'
                    : 'Informe a quantidade que saiu fora do fluxo de manutenção. O sistema vai subtrair do estoque atual.',
                autoCloseTime: 3,
                ehDestacado: true,
            }}
            apiConfig={{
                endpoint: 'Estoque',
                requisicaoBemSucedida: () => { /* não usado - onSave customizado */ },
                tratarErro: () => { /* não usado - onSave customizado */ },
                idTenentCampo: '',
            }}
        />
    );
}
